import type {
  AvailabilitySlot,
  CalendarEventRecord,
} from "@hydeck/mdt-contracts";
import {
  createEffect,
  createMemo,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { DumpIcon } from "../components/DumpIcon";
import { apiRequest } from "../core/api";
import { currentUser } from "../core/session";
import type { ResourceChangedDetail } from "../core/realtime";

const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const apiWeekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const monthNames = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

type CalendarCell = { date: Date; currentMonth: boolean };

export function CalendarPage() {
  const today = new Date();
  const [visibleMonth, setVisibleMonth] = createSignal(
    new Date(today.getFullYear(), today.getMonth(), 1),
  );
  const [view, setView] = createSignal<"Calendar" | "Availability">("Calendar");
  const [timeMode, setTimeMode] = createSignal<"UTC" | "LOCAL" | "HUMAN">("UTC");
  const [events, setEvents] = createSignal<CalendarEventRecord[]>([]);
  const [availability, setAvailability] = createSignal(new Set<string>());
  const [availabilityDirty, setAvailabilityDirty] = createSignal(false);
  const [eventModal, setEventModal] = createSignal(false);
  const [activeEvent, setActiveEvent] = createSignal<CalendarEventRecord | null>(
    null,
  );
  const [error, setError] = createSignal("");

  const cells = createMemo(() => calendarCells(visibleMonth()));

  const loadEvents = async () => {
    const date = visibleMonth();
    try {
      setEvents(
        await apiRequest<CalendarEventRecord[]>(
          `/events/${date.getFullYear()}/${date.getMonth()}`,
          { token: currentUser()?.accessToken },
        ),
      );
      setError("");
    } catch (reason) {
      setError(message(reason));
      setEvents([]);
    }
  };

  const loadAvailability = async () => {
    try {
      const slots = await apiRequest<AvailabilitySlot[]>("/availability", {
        token: currentUser()?.accessToken,
      });
      setAvailability(
        new Set(slots.map((slot) => `${slot.dayOfWeek}-${slot.hour}`)),
      );
      setAvailabilityDirty(false);
    } catch (reason) {
      setError(message(reason));
    }
  };

  createEffect(() => {
    visibleMonth();
    void loadEvents();
  });

  onMount(() => {
    const refresh = (event: Event) => {
      const route = (event as CustomEvent<ResourceChangedDetail>).detail.route;
      if (route.startsWith("/events")) void loadEvents();
      if (route.startsWith("/availability")) void loadAvailability();
    };
    window.addEventListener("mdt:resource-changed", refresh);
    onCleanup(() =>
      window.removeEventListener("mdt:resource-changed", refresh),
    );
  });
  createEffect(() => {
    if (view() === "Availability") void loadAvailability();
  });

  const changeMonth = (offset: number) => {
    const current = visibleMonth();
    setVisibleMonth(
      new Date(current.getFullYear(), current.getMonth() + offset, 1),
    );
  };

  const cycleTimeMode = () =>
    setTimeMode((mode) =>
      mode === "UTC" ? "LOCAL" : mode === "LOCAL" ? "HUMAN" : "UTC",
    );

  const eventsFor = (date: Date) =>
    events().filter((event) => {
      const startsAt = new Date(event.startsAt);
      return (
        startsAt.getFullYear() === date.getFullYear() &&
        startsAt.getMonth() === date.getMonth() &&
        startsAt.getDate() === date.getDate()
      );
    });

  const toggleSlot = (day: number, hour: number) => {
    const next = new Set(availability());
    const key = `${day}-${hour}`;
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setAvailability(next);
    setAvailabilityDirty(true);
  };

  const saveAvailability = async () => {
    const slots = [...availability()].map((key) => {
      const [dayOfWeek, hour] = key.split("-").map(Number);
      return { dayOfWeek: dayOfWeek!, hour: hour! };
    });
    await apiRequest("/availability", {
      method: "PUT",
      token: currentUser()?.accessToken,
      body: { slots },
    });
    setAvailabilityDirty(false);
  };

  return (
    <div class="flex h-full flex-col">
      <div class="mb-4 flex h-11 items-center justify-between bg-[rgba(255,255,255,.08)] pl-4 pr-2 leading-none">
        <div class="flex items-center">
          <button
            class="flex h-9 w-9 items-center justify-center text-gray-400 hover:text-gray-100"
            onClick={() => changeMonth(-1)}
          >
            <Chevron direction="left" />
          </button>
          <button class="px-3.5 text-sm font-semibold text-white">
            {monthNames[visibleMonth().getMonth()]} {visibleMonth().getFullYear()}
          </button>
          <button
            class="flex h-9 w-9 items-center justify-center text-gray-400 hover:text-gray-100"
            onClick={() => changeMonth(1)}
          >
            <Chevron direction="right" />
          </button>
        </div>

        <div class="flex items-center gap-4">
          <Show when={view() === "Calendar"}>
            <button
              class="flex h-7 items-center bg-[rgba(1,137,102,.25)] px-2 text-sm hover:bg-[rgba(1,137,102,.75)]"
              onClick={() => {
                setActiveEvent(null);
                setEventModal(true);
              }}
            >
              <DumpIcon name="plus" />
              <span class="pl-2">Create Event</span>
            </button>
            <button
              class="text-sm font-semibold text-white"
              onClick={cycleTimeMode}
            >
              Current time display mode: {timeMode()}
            </button>
          </Show>
          <div class="flex items-center gap-1 rounded-lg bg-[rgba(255,255,255,.05)] p-1">
            <ViewButton
              active={view() === "Calendar"}
              label="Calendar"
              onClick={() => setView("Calendar")}
            />
            <ViewButton
              active={view() === "Availability"}
              label="Availability"
              onClick={() => setView("Availability")}
            />
          </div>
        </div>
      </div>

      <Show when={error()}>
        <div class="mb-3 border border-red-500/30 bg-red-950/30 px-3 py-2 text-sm text-red-300">
          {error()}
        </div>
      </Show>

      <Show
        when={view() === "Calendar"}
        fallback={
          <AvailabilityGrid
            slots={availability()}
            dirty={availabilityDirty()}
            onToggle={toggleSlot}
            onSave={() => void saveAvailability()}
            onClear={() => {
              setAvailability(new Set<string>());
              setAvailabilityDirty(true);
            }}
            onWeekdays={() => {
              const next = new Set<string>();
              for (let day = 1; day <= 5; day += 1) {
                for (let hour = 9; hour < 17; hour += 1) {
                  next.add(`${day}-${hour}`);
                }
              }
              setAvailability(next);
              setAvailabilityDirty(true);
            }}
          />
        }
      >
        <div class="flex flex-auto flex-col overflow-hidden">
          <div class="grid grid-cols-7 gap-px border-b border-gray-500 text-center text-xs font-semibold leading-6 text-gray-300">
            <For each={weekDays}>{(day) => <div class="py-2">{day}</div>}</For>
          </div>
          <div class="flex-1 overflow-auto">
            <div class="grid min-h-full w-full grid-cols-7 grid-rows-6 text-xs leading-6">
              <For each={cells()}>
                {(cell) => (
                  <div
                    class={`relative min-h-28 border border-gray-500 px-3 py-2 ${
                      cell.currentMonth ? "text-gray-100" : "text-gray-500"
                    }`}
                  >
                    <time
                      class={
                        isToday(cell.date)
                          ? "flex h-6 w-6 items-center justify-center rounded-md bg-green-gradient font-semibold"
                          : "flex h-6 w-6 items-center justify-center rounded-md font-semibold"
                      }
                    >
                      {cell.date.getDate()}
                    </time>
                    <ol class="mt-2 max-h-24 overflow-y-auto">
                      <For each={eventsFor(cell.date)}>
                        {(event) => (
                          <li
                            class="group mb-1 flex cursor-pointer bg-[rgba(1,137,102,.2)] px-2 py-1 roboto500 hover:bg-[rgba(1,137,102,.55)]"
                            onClick={() => {
                              setActiveEvent(event);
                              setEventModal(true);
                            }}
                          >
                            <strong class="flex-auto truncate">
                              {event.title}
                            </strong>
                            <time>{formatTime(event.startsAt, timeMode())}</time>
                          </li>
                        )}
                      </For>
                    </ol>
                  </div>
                )}
              </For>
            </div>
          </div>
        </div>
      </Show>

      <Show when={eventModal()}>
        <EventModal
          event={activeEvent()}
          onClose={(changed) => {
            setEventModal(false);
            setActiveEvent(null);
            if (changed) void loadEvents();
          }}
        />
      </Show>
    </div>
  );
}

function AvailabilityGrid(props: {
  slots: Set<string>;
  dirty: boolean;
  onToggle: (day: number, hour: number) => void;
  onSave: () => void;
  onClear: () => void;
  onWeekdays: () => void;
}) {
  return (
    <div class="flex h-full flex-col overflow-hidden rounded-md border border-white-5 bg-gray-gradient">
      <div class="flex items-center justify-between border-b border-white-5 bg-black/25 px-4 py-3">
        <p class="text-xs font-medium">My Availability</p>
        <div class="flex gap-2">
          <button class="bg-white-5 px-3 py-1 text-xs" onClick={props.onWeekdays}>
            Weekdays 9-5
          </button>
          <button class="bg-white-5 px-3 py-1 text-xs" onClick={props.onClear}>
            Clear All
          </button>
          <button
            class="border border-cyan/30 bg-green-gradient px-3 py-1 text-xs disabled:opacity-40"
            disabled={!props.dirty}
            onClick={props.onSave}
          >
            Save
          </button>
        </div>
      </div>
      <div class="flex-1 overflow-auto p-3">
        <div
          class="grid min-w-[600px] select-none overflow-hidden rounded border border-white-5 bg-black/20"
          style={{ "grid-template-columns": "48px repeat(7, 1fr)" }}
        >
          <div class="h-8 bg-black/30" />
          <For each={apiWeekDays}>
            {(day) => (
              <div class="flex h-8 items-center justify-center border-l border-white-5 bg-black/30 text-xs">
                {day}
              </div>
            )}
          </For>
          <For each={Array.from({ length: 24 }, (_, hour) => hour)}>
            {(hour) => (
              <>
                <div class="flex h-6 items-center justify-end border-t border-white-5 bg-black/15 pr-2 text-[10px] text-white-35">
                  {hourLabel(hour)}
                </div>
                <For each={Array.from({ length: 7 }, (_, day) => day)}>
                  {(day) => (
                    <button
                      class={`h-6 border-l border-t border-white-5 ${
                        props.slots.has(`${day}-${hour}`)
                          ? "bg-[radial-gradient(circle,rgba(0,248,185,.45),rgba(1,137,102,.35))]"
                          : "bg-transparent hover:bg-cyan/10"
                      }`}
                      onClick={() => props.onToggle(day, hour)}
                    />
                  )}
                </For>
              </>
            )}
          </For>
        </div>
      </div>
    </div>
  );
}

function EventModal(props: {
  event: CalendarEventRecord | null;
  onClose: (changed: boolean) => void;
}) {
  const [form, setForm] = createSignal({
    title: props.event?.title ?? "",
    description: props.event?.description ?? "",
    startsAt: toLocalInput(props.event?.startsAt),
    isVisiblePublic: props.event?.isVisiblePublic ?? false,
    isVisiblePolice: props.event?.isVisiblePolice ?? true,
    isVisibleMedical: props.event?.isVisibleMedical ?? false,
    isVisibleDoj: props.event?.isVisibleDoj ?? true,
  });
  const [error, setError] = createSignal("");
  const save = async () => {
    const value = form();
    if (!value.title.trim() || !value.startsAt) return;
    if (
      !value.isVisiblePublic &&
      !value.isVisiblePolice &&
      !value.isVisibleMedical &&
      !value.isVisibleDoj
    ) {
      setError("Select at least one visibility group.");
      return;
    }
    const body = {
      ...value,
      startsAt: new Date(value.startsAt).toISOString(),
      endsAt: null,
    };
    if (props.event) {
      const date = new Date(props.event.startsAt);
      await apiRequest(
        `/events/${date.getFullYear()}/${date.getMonth() + 1}/${props.event.id}`,
        {
          method: "PATCH",
          token: currentUser()?.accessToken,
          body,
        },
      );
    } else {
      await apiRequest("/events", {
        method: "POST",
        token: currentUser()?.accessToken,
        body,
      });
    }
    props.onClose(true);
  };
  const remove = async () => {
    if (!props.event) return;
    const date = new Date(props.event.startsAt);
    await apiRequest(
      `/events/${date.getFullYear()}/${date.getMonth() + 1}/${props.event.id}`,
      { method: "DELETE", token: currentUser()?.accessToken },
    );
    props.onClose(true);
  };
  return (
    <div class="modal fixed inset-0 z-[1000] flex items-center justify-center bg-black/65">
      <div class="relative min-w-[33rem] border border-white-11 bg-gray-gradient p-5 shadow-green-gradient">
        <div class="mb-5 flex items-center justify-between">
          <p class="roboto700 text-2xl">
            {props.event ? "Edit event" : "Create event"}
          </p>
          <button
            class="flex h-9 w-9 items-center justify-center bg-red-gradient"
            onClick={() => props.onClose(false)}
          >
            <DumpIcon name="tab_close" />
          </button>
        </div>
        <div class="flex flex-col gap-3">
          <input
            class="min-h-11 bg-white-11 px-3"
            placeholder="Title"
            value={form().title}
            onInput={(event) =>
              setForm((item) => ({ ...item, title: event.currentTarget.value }))
            }
          />
          <input
            class="min-h-11 bg-white-11 px-3"
            placeholder="Description"
            value={form().description}
            onInput={(event) =>
              setForm((item) => ({
                ...item,
                description: event.currentTarget.value,
              }))
            }
          />
          <input
            class="min-h-11 bg-white-11 px-3"
            type="datetime-local"
            value={form().startsAt}
            onInput={(event) =>
              setForm((item) => ({
                ...item,
                startsAt: event.currentTarget.value,
              }))
            }
          />
          <For
            each={[
              ["isVisiblePublic", "Visible for public"],
              ["isVisiblePolice", "Visible for police"],
              ["isVisibleMedical", "Visible for medical"],
              ["isVisibleDoj", "Visible for DOJ"],
            ] as const}
          >
            {([key, label]) => (
              <label class="flex items-center justify-between">
                <span>{label}</span>
                <input
                  type="checkbox"
                  checked={form()[key]}
                  onChange={(event) =>
                    setForm((item) => ({
                      ...item,
                      [key]: event.currentTarget.checked,
                    }))
                  }
                />
              </label>
            )}
          </For>
          <Show when={error()}>
            <p class="text-sm text-red-400">{error()}</p>
          </Show>
          <div class="mt-3 flex justify-end gap-2">
            <Show when={props.event}>
              <button class="bg-red-gradient px-5 py-2" onClick={() => void remove()}>
                Delete
              </button>
            </Show>
            <button class="bg-green-gradient px-5 py-2" onClick={() => void save()}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ViewButton(props: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      class={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${
        props.active
          ? "bg-[rgba(1,137,102,.45)] text-cyan"
          : "text-gray-400 hover:bg-white-5"
      }`}
      onClick={props.onClick}
    >
      <span>{props.label}</span>
    </button>
  );
}

function calendarCells(month: Date): CalendarCell[] {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const firstDay = new Date(year, monthIndex, 1);
  const mondayOffset = (firstDay.getDay() + 6) % 7;
  const firstCell = new Date(year, monthIndex, 1 - mondayOffset);
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(
      firstCell.getFullYear(),
      firstCell.getMonth(),
      firstCell.getDate() + index,
    );
    return { date, currentMonth: date.getMonth() === monthIndex };
  });
}

function isToday(date: Date) {
  const today = new Date();
  return date.toDateString() === today.toDateString();
}

function formatTime(
  input: string,
  mode: "UTC" | "LOCAL" | "HUMAN",
): string {
  const date = new Date(input);
  if (mode === "UTC") {
    return `${String(date.getUTCHours()).padStart(2, "0")}:${String(
      date.getUTCMinutes(),
    ).padStart(2, "0")} UTC`;
  }
  if (mode === "LOCAL") {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  const minutes = Math.round((date.getTime() - Date.now()) / 60_000);
  if (Math.abs(minutes) < 60) return `${Math.abs(minutes)}m ${minutes >= 0 ? "from now" : "ago"}`;
  const hours = Math.round(Math.abs(minutes) / 60);
  return `${hours}h ${minutes >= 0 ? "from now" : "ago"}`;
}

function hourLabel(hour: number): string {
  if (hour === 0) return "12 AM";
  if (hour === 12) return "12 PM";
  return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
}

function toLocalInput(input?: string): string {
  if (!input) return "";
  const date = new Date(input);
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function Chevron(props: { direction: "left" | "right" }) {
  return (
    <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
      <path
        d={props.direction === "left" ? "M7 1 1 7l6 6" : "M1 1l6 6-6 6"}
        stroke="currentColor"
        stroke-width="2"
      />
    </svg>
  );
}

function message(reason: unknown): string {
  return reason instanceof Error ? reason.message : "Request failed";
}
